import { useState, useEffect, useCallback } from "react"

import { paths } from "src/routes/paths"
import { useRouter, useSearchParams } from "src/routes/hooks"

import { CONFIG } from "src/config-global"

import { SplashScreen } from "src/common/loading-screen"

import { useAuthStore } from "@/src/store"

// ----------------------------------------------------------------------

type Props = {
    children: React.ReactNode
}

export function AuthGuard({ children }: Props) {
    const router = useRouter()

    const searchParams = useSearchParams()

    const authenticated = useAuthStore((state) => state.authenticated)
    const loading = useAuthStore((state) => state.loading)

    const [isChecking, setIsChecking] = useState<boolean>(true)

    const createReturnToString = useCallback(() => {
        const params = new URLSearchParams()
        searchParams?.forEach((v, n) => {
            params.set(n, v)
        })

        const location = window.location.pathname.replace(/\/$/, "")
        const paramsStr = params.toString()

        return `returnTo=${encodeURIComponent(`${location}${paramsStr ? `?${paramsStr}` : ""}`)}`
    }, [searchParams])

    const checkPermissions = async (): Promise<void> => {
        if (loading) {
            return
        }

        if (!authenticated) {
            const { method } = CONFIG.auth

            const signInPath = {
                jwt: paths.auth.jwt.signIn,
                auth0: paths.auth.auth0.signIn,
                amplify: paths.auth.amplify.signIn,
                firebase: paths.auth.firebase.signIn,
                supabase: paths.auth.supabase.signIn,
            }[method]

            const href = `${signInPath}?${createReturnToString()}`

            router.replace(href)
            return
        }

        setIsChecking(false)
    }

    useEffect(() => {
        checkPermissions()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authenticated, loading])

    if (isChecking) {
        return <SplashScreen />
    }

    return <>{children}</>
}
